import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ErrorService } from '@app/common/services/error.service';
import { CompositeFilterDescriptor, FilterDescriptor, State, toODataString } from '@progress/kendo-data-query';
import { Table } from 'app/shared/models';
import { throwError as observableThrowError, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ColumnMapperInterface } from './column-mapper';
import { GridApiContext } from './grid-api-context';

export class DataApiContext<t> extends Promise<t[]> {
  private tableName: string;
  private tableIDField: string;
  public allowErrors: boolean = false;

  constructor(
    private http: HttpClient,
    private BASE_URL: string,
    private table: Table | string,
    private mapper: ColumnMapperInterface<t>,
    private errorService?: ErrorService,
    private isOdataRequest: boolean = true
  ) {
    super(null);
    if (typeof table === 'string') {
      this.tableName = table;
    } else {
      this.tableName = Table[table];
    }
  }

  private fetch(tableName: string, limitColumnsToMapped: boolean = false, stateOrFilter?: State | string, expand: string = '',
    selectSource: object = null, selectStatement: string = '', cache: Map<string, t[]> = null): Promise<t[]> {
    let queryStr: string = '';

    if (expand == null) expand = '';
    if (selectStatement == null) selectStatement = '';

    let skipOdata = false;
    if (stateOrFilter && typeof stateOrFilter === 'string') {
      queryStr = stateOrFilter as string;
      skipOdata = true;
    }

    if (!skipOdata && this.isOdataRequest) {
      if (expand && expand.length) {
        if (expand.startsWith('expand=')) {
          expand = '&$' + expand;
        } else if (expand.startsWith('$expand=')) {
          expand = '&' + expand;
        }
        if (!expand.startsWith('&$expand=')) {
          expand = '&$expand=' + expand;
        }
      }

      if (stateOrFilter) {
        queryStr = `${toODataString(stateOrFilter as State)}&$count=true${expand}`;
      } else {
        queryStr = `$skip=0&$count=true${expand}`;
      }

      if (selectStatement && selectStatement.length) {
        if (selectStatement.startsWith('select=')) {
          selectStatement = '&$' + selectStatement;
        } else if (selectStatement.startsWith('$select=')) {
          selectStatement = '&' + selectStatement;
        }
        if (!selectStatement.startsWith('&$select=')) {
          selectStatement = '&$select=' + selectStatement;
        }
      }
    }

    let mapper = this.mapper;
    if (limitColumnsToMapped && this.mapper && this.mapper.getSelect) {
      queryStr += this.mapper.getSelect(selectSource);
    } else {
      queryStr += selectStatement;
    }
    var fullUrl = `${this.BASE_URL}${tableName}?${queryStr}`;
    if (cache && cache.has(fullUrl)) {
      return Promise.resolve(cache.get(fullUrl));
    }
    var promise = this.http.get(fullUrl).pipe(
      catchError((e) => {

        let fullMessage: string;
        let message: string;
        let error: Error;
        if (e.status === 404) {
          fullMessage = 'Table [' + tableName + '] not known to API';
        } else {
          let errorMsg = e.statusText || 'Server error';
          try {
            let x = e as any;
            errorMsg = (x.error && (x.error.message || (x.error.error && x.error.error.message))) || x.message;
          } catch (ce) {
          }
          message = `Table [${tableName}] error.`;
          if (queryStr) {
            errorMsg += ` Query=${queryStr}.`;
          }
          error = new Error(errorMsg);
          fullMessage = `Table [${tableName}] error: ${errorMsg}`;
        }
        if (!this.allowErrors) {
          this.errorService.error(message, error);
          return [];
        } else {
          return observableThrowError(fullMessage);
        }
      }),
      map(response => {
        let json = response as any;
        let data: any;
        if (this.isOdataRequest) {
          data = json.value;
        } else {
          data = json;
        }
        if (mapper && mapper.map && data.map) {
          if (mapper.setupMap) {
            mapper.setupMap(this.table);
          }
          let m = mapper;
          data = data.map(e => {
            return m.map(e);
          });
          return data as t[];
        } else if (data.length) {
          return data as t[];
        } else {
          if (mapper && mapper.map) {
            data = mapper.map(data);
          }
          let list = new Array<t>();
          list.push(data);
          return list;
        }
      }
      ),).toPromise();

    if (cache) {
      promise.then(x => {
        cache.set(fullUrl, x);
      })
    }
    return promise;
  }

  public eq(): string {
    return 'eq';
  }

  public inFilter(field: string, values: any[]): CompositeFilterDescriptor {
    let filters: Array<FilterDescriptor> = [];
    for (let i = 0; i < values.length; i++) {
      let one = values[i];
      if (one) {
        let filter: FilterDescriptor = { field: field, operator: 'eq', value: one };
        filters.push(filter);
      }
    }
    let fd: CompositeFilterDescriptor = { logic: 'or', filters: filters };
    return fd;
  }

  public query(limitColumnsToMapped: boolean = false, state?: State | string, expand: string = '', selectSource: object = null, selectStatement: string = '', cache: Map<string, t[]> = null): Promise<t[]> {
    return this.fetch(this.tableName, limitColumnsToMapped, state, expand, selectSource, selectStatement, cache);
  }

  private create(data: t, urlParams: string = '') {
    if (data && this.tableIDField) {
      data[this.tableIDField] = 0;
    }
    const queryStr = this.BASE_URL;

    let url = `${this.BASE_URL}${this.tableName}`;
    if (urlParams) {
      if (!urlParams.startsWith('?')) {
        urlParams = '?' + urlParams;
      }
      url += urlParams;
    }
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post(url, data, { headers: headers }).pipe(
        catchError((e) => this.errorService.handleHTTPError(e)));
  }

  private update(data: t, full: boolean = false, urlParams: string = ''): Promise<Object> {
    const queryStr = this.BASE_URL;
    let url = `${this.BASE_URL}${this.tableName}`;
    if (this.isOdataRequest) {
      url += `(${data[this.tableIDField]})`;
    }

    if (urlParams) {
      if (!urlParams.startsWith('?')) {
        urlParams = '?' + urlParams;
      }
      url += urlParams;
    }

    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (full) {
      return this.http.put(url, data, { headers: headers }).pipe(
        catchError((e) => this.errorService.handleHTTPError(e))).toPromise();
    } else {
      return this.http.patch(url, data, { headers: headers }).pipe(
        catchError((e) => this.errorService.handleHTTPError(e))).toPromise();
    }
  }

  public post(data: t, removeObjects: boolean = false, urlParams: string[] | string | any = null) {
    return this.save(data, true, removeObjects, urlParams);
  }

  public save(data: t, isNew?: boolean, removeObjects: boolean = false, urlParams: string[] | string | any = null) {
    let copy = data;
    if (removeObjects) {
      copy = Object.assign({}, data);
      GridApiContext.removeObjects(copy);
    }

    let urlParamStr = '';

    if (urlParams !== null && urlParams !== undefined) {
      let urlParamPieces: string[] = [];

      if (typeof urlParams === 'string') {
        urlParamStr = urlParams;
      } else if (Array.isArray(urlParams)) {
        urlParamPieces = urlParams as string[];
      }
      else {
        let urlObject = urlParams as object;
        for (let prop in urlObject) {
          if (urlObject.hasOwnProperty(prop)) {
            let valof = urlObject[prop];
            if (valof !== null && valof !== undefined) {
              urlParamPieces.push(`${prop}=${valof}`);
            }
          }
        }
      }
      if (urlParamPieces && urlParamPieces.length) {
        urlParamStr = urlParamPieces.join('&');
      }
    }

    if (isNew) {
      let result = this.create(copy, urlParamStr);
      result.pipe(map<any, any>((r, index: number) => {
        if (r.ok) {
          let m = r.then(v => {
            if (data) {
              data[this.tableIDField] = v[this.tableIDField];
            }
          });
        }
        return r;
      }));
      return result;
    } else {
      return this.update(copy, true, urlParamStr);
    }
  }

  protected _post(endpoint: string, data: t): Promise<Object> {
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    let options = new HttpParams({});
    let response = this.http
      .post(url, data, { headers: headers }).pipe(
        catchError((err) => this.errorService.handleHTTPError(err)
        ));
    return response.toPromise();
  }

  protected filterToString({ filter }: { filter?: string }): string {
    return (filter && typeof filter === 'string') ? `&$filter=${filter}` : '';
  }
}

export class LongRunningProcess {
  constructor(public processKey: string, public subscription: Subscription) { }
}